import { lazy, ComponentType } from 'react';
import { RouteObject } from 'react-router-dom';
import { eventBus } from './event-bus';

// 插件元信息
export interface PluginMeta {
  name: string;
  version: string;
  displayName: string;
  description?: string;
  icon?: string;
  category?: string;
}

// 侧边栏菜单项
export interface SidebarMenuItem {
  id: string;
  title: string;
  icon?: string;
  path?: string;
  order?: number;
  children?: SidebarMenuItem[];
  badge?: string | number;
}

// AI 动作定义
export interface AIAction {
  id: string;
  namespace: string;
  name: string;
  description: string;
  parameters?: AIActionParameter[];
  handler: (params: Record<string, unknown>, context: AIActionContext) => Promise<AIActionResult>;
  confirm?: boolean;
}

export interface AIActionParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  required?: boolean;
  default?: unknown;
}

export interface AIActionContext {
  currentRoute: string;
  selectedItems?: unknown[];
  user: unknown;
}

export interface AIActionResult {
  type: 'text' | 'table' | 'chart' | 'report' | 'action' | 'error' | 'list' | 'content';
  data: unknown;
  message?: string;
}

// 前端插件接口
export interface FrontendPlugin {
  meta: PluginMeta;
  
  // 生命周期
  onLoad?: () => void | Promise<void>;
  onUnload?: () => void;
  
  // 路由配置
  routes?: RouteObject[];
  
  // 侧边栏菜单
  sidebarMenus?: SidebarMenuItem[];
  
  // AI 动作
  aiActions?: AIAction[];
  
  // 全局组件
  globalComponents?: {
    id: string;
    component: ComponentType;
    position: 'header' | 'sidebar' | 'footer' | 'toolbar';
  }[];
}

// 插件注册表
export class PluginRegistry {
  private plugins: Map<string, FrontendPlugin> = new Map();
  private routes: RouteObject[] = [];
  private sidebarMenus: SidebarMenuItem[] = [];
  private aiActions: Map<string, AIAction> = new Map();

  /**
   * 注册插件
   */
  register(plugin: FrontendPlugin): void {
    const { name } = plugin.meta;
    
    if (this.plugins.has(name)) {
      console.warn(`Plugin "${name}" is already registered. Skipping.`);
      return;
    }

    this.plugins.set(name, plugin);

    // 注册路由
    if (plugin.routes) {
      this.routes.push(...plugin.routes);
    }

    // 注册侧边栏菜单
    if (plugin.sidebarMenus) {
      this.sidebarMenus.push(...plugin.sidebarMenus);
      // 按 order 排序
      this.sidebarMenus.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    }

    // 注册 AI 动作
    if (plugin.aiActions) {
      for (const action of plugin.aiActions) {
        const actionId = `${action.namespace}:${action.id}`;
        this.aiActions.set(actionId, action);
      }
    }

    console.log(`Plugin "${name}" registered successfully.`);
    eventBus.emit('plugin:loaded', { name });
  }

  /**
   * 初始化所有插件
   */
  async initAll(): Promise<void> {
    for (const [name, plugin] of this.plugins) {
      if (plugin.onLoad) {
        try {
          await plugin.onLoad();
          console.log(`Plugin "${name}" initialized.`);
        } catch (error) {
          console.error(`Failed to initialize plugin "${name}":`, error);
        }
      }
    }
  }

  /**
   * 获取所有路由
   */
  getRoutes(): RouteObject[] {
    return this.routes;
  }

  /**
   * 获取所有侧边栏菜单
   */
  getSidebarMenus(): SidebarMenuItem[] {
    return this.sidebarMenus;
  }

  /**
   * 获取所有 AI 动作
   */
  getAIActions(): AIAction[] {
    return Array.from(this.aiActions.values());
  }

  /**
   * 根据 ID 获取 AI 动作
   */
  getAIAction(actionId: string): AIAction | undefined {
    return this.aiActions.get(actionId);
  }

  /**
   * 获取插件
   */
  getPlugin(name: string): FrontendPlugin | undefined {
    return this.plugins.get(name);
  }

  /**
   * 获取所有插件
   */
  getAllPlugins(): FrontendPlugin[] {
    return Array.from(this.plugins.values());
  }
}

// 全局插件注册表实例
export const pluginRegistry = new PluginRegistry();

// 懒加载组件辅助函数
export function lazyLoadComponent(
  importFn: () => Promise<{ default: ComponentType }>
): ComponentType {
  return lazy(importFn);
}

// 定义插件的辅助函数
export function definePlugin(plugin: FrontendPlugin): FrontendPlugin {
  return plugin;
}

// 定义 AI 动作的辅助函数
export function defineAIActions(config: {
  namespace: string;
  actions: Omit<AIAction, 'namespace'>[];
}): AIAction[] {
  return config.actions.map((action) => ({
    ...action,
    namespace: config.namespace,
  }));
}

